<template>
  <panel-layout
    id="product-panel"
    :loading="loading"
    :shouldDismiss="() => !unsavedChanges"
    :didAttemptToDismiss="cancel"
  >
    <template #heading>
      <div v-if="product" class="flex items-center space-x-2 mb-1">
        <!-- <div v-if="!product.isNewRecord" class="w-18 h-18 relative">
          <o-avatar :items="[ product ]" size="xl" />
          <div class="inset-0 absolute rounded-full h-18 w-18 bg-black/15 backdrop-filter backdrop-blur-sm flex items-center justify-center opacity-0 hover:opacity-100 transition cursor-pointer" @click="handleEditImage">
            <o-icon icon="edit" class="text-white" />
          </div>
        </div> -->
        <div>
          <h2 class="text-lg md:text-xl tracking-tight font-bold text-gray-800/85 leading-none dark:text-gray-100">
            {{ product.isNewRecord ? $t('product.new') : product.name }}
          </h2>
          <div v-if="!product.isNewRecord" class="mt-1">
            <p class="text-gray-700 text-sm md:text-base dark:text-gray-200" v-html="$t('pages.product.edit.id', { id: product.idProduct })" />
          </div>
        </div>
      </div>
    </template>

    <template #actions-right>
      <div v-if="product && !product.isNewRecord" class="flex items-center space-x-1">
        <o-tooltip :text="$t('general.delete')">
          <o-button icon="delete" flat @click="handleDelete" />
        </o-tooltip>
      </div>
    </template>

    <o-form v-if="product" ref="form" class="h-full w-full">
      <o-tabs
        ref="tabsRef"
        nav-class="bg-gray-50 h-11 border-b px-2 dark:bg-gray-900"
        scroll
      >
        <o-tab :name="$t('general.details')" icon="details">
          <product-details v-model:product="product" @updateImageSuccess="handleUpdateImageSuccess" />
        </o-tab>
        <o-tab :name="$t('general.inventory')" icon="stockTake">
          <product-inventory v-model:product="product" />
        </o-tab>
        <o-tab :name="$t('general.purchaseOrders')" icon="purchaseOrder">
          <product-purchase-order v-model:product="product" />
        </o-tab>
      </o-tabs>
    </o-form>

    <template #footer>
      <div v-if="product">
        <div class="flex justify-between align-items">
          <div class="flex items-center space-x-1">
            <o-button outline flat class="mr-2" @click="cancel()">
              {{ $t('general.cancel') }}
            </o-button>
          </div>
          <div class="flex items-center space-x-1">
            <o-button variant="primary" outline icon="save" class="mr-2" @click="save()">
              {{ $t('general.save') }}
            </o-button>
            <o-button variant="primary" icon="save" class="mr-2" @click="save(true)">
              {{ $t('general.saveAndClose') }}
            </o-button>
          </div>
        </div>
      </div>
    </template>

    <template v-if="product">
      <!-- <update-image-dialog
        ref="updateImageDialog"
        :product="product"
        @success="fetchProduct"
      /> -->

      <deleteDialog
        ref="deleteDialog"
        @success="handleDeleteSuccess"
      />
    </template>

    <o-dialog
      ref="confirmDialog"
      :title="$t('general.unsavedChanges')"
      :info="$t('general.unsavedChangesInfo')"
      icon="product"
      :options="[
        { text: $t('general.back'), type: 'cancel' },
        { text: $t('general.closeWithoutSaving'), type: 'confirm', variant: undefined, flat: true, outline: true, callback: () => cancel(true) },
        { text: $t('general.saveAndClose'), type: 'confirm', response: 'save', callback: () => save(true) }
      ]"
      :portal="panel.portal"
    />
  </panel-layout>
</template>
<script setup lang="ts">
import ProductDetails from '@/components/product/edit/Details.vue'
import ProductInventory from '@/components/product/edit/Inventory.vue'
import ProductPurchaseOrder from '@/components/product/edit/PurchaseOrder.vue'

// @ts-ignore
import DeleteDialog from '@/components/product/actions/Delete.vue'

// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'

import {
  OTabs,
  OTab,
  OAlert,
  OAvatar,
} from '@/components/elements'

import { usePanel } from '~/composables/usePanel'

const panel = usePanel()

const props = withDefaults(defineProps<{
  id?: string
  category?: string | number | Record<string, any> | null
}>(), {
  category: null,
})

const { $api, $notification, $bus, $i18n } = useNuxtApp()
const router = useRouter()

const product = ref<any>(null)
const errors = ref<any>(false)
const loading = ref(true)

const confirmDialog = ref<any>()
const deleteDialog = ref<any>()
const form = ref<any>()
const tabsRef = ref<any>()

const { unsavedChanges, snapshot } = useUnsavedChanges(product)

const fetchProduct = async (id?: string) => {
  loading.value = true

  try {
    if (!id) {
      product.value = (await $api.product.defaults({} as any)).data
      product.value.category = props.category
    } else {
      product.value = (await $api.product.get(id)).data
    }
    snapshot()
  } catch (e) {
    console.log(e)
  }

  loading.value = false
}

const save = async (close = false) => {
  if (!await validateForm()) return false

  loading.value = true
  errors.value = false

  try {
    const isNewRecord = product.value.isNewRecord
    const _product = (await $api.product.save(product.value)).data

    if (_product) {
      product.value = _product
      snapshot()

      $notification.toast({
        title: $i18n.t('pages.product.edit.success'),
        text: $i18n.t('pages.product.edit.succeessInfo'),
        variant: 'success',
      })

      $bus.emit.updated('Product', _product)

      if (close) {
        panel.close()
      } else if (isNewRecord) {
        // Swap in-place to the edit view of the freshly-created product.
        panel.replace('product/edit', { id: String(_product.idProduct) })
      }
    }
  } catch (e: any) {
    errors.value = e?.errors
    loading.value = false

    $notification.error({ errors: e.errors })
    return false
  }

  loading.value = false
}

const validateForm = async () => {
  const previousTab = tabsRef.value?.getActiveTab()?.hash

  tabsRef.value?.selectTabByIndex(0)
  await nextTick()

  const valid = await form.value?.validate()

  if (valid && previousTab) {
    tabsRef.value?.selectTab(previousTab)
  }

  return Boolean(valid)
}

const cancel = (force = false) => {
  if (!force && unsavedChanges.value) {
    confirmDialog.value.open()
  } else {
    panel.close(force)
  }
}

const handleDelete = () => {
  deleteDialog.value.open(product.value)
}

const handleDeleteSuccess = () => {
  panel.close()
}

const handleUpdateImageSuccess = () => {
  fetchProduct(props.id)
  $bus.emit.updated('Product')
}

watch(() => props.id, (id) => fetchProduct(id), { immediate: true })
</script>
